import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CalendarDays, MapPin, Sparkles, Check } from "lucide-react";
import { useLang, scrollToId } from "@/i18n";
import { CONFIG, UNITS } from "@/staticData";
import { EVENTS } from "@/eventsData";
import { waLink } from "@/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import BookingSection from "@/components/BookingSection";

// Conținut editorial per eveniment (ro/en/de/it; he/pl -> fallback en)
const PAGES = {
  "fis-grand-prix-rasnov": {
    ro: {
      eyebrow: "EVENIMENT MAJOR · 19–20 SEPTEMBRIE 2026",
      title: "EI ZBOARĂ. TU DORMI LA IMPERFECT HOUSE.",
      lead: "Calendarul oficial FIS confirmă: pe 19 și 20 septembrie, cei mai buni săritori cu schiurile ai lumii vin la Râșnov, pe trambulina din Valea Cărbunării — masculin și feminin, Grand Prix. Competiția e la câteva minute de casă.",
      highlights: ["Grand Prix FIS — masculin și feminin", "Trambulina Valea Cărbunării, Râșnov", "Cei mai buni săritori ai lumii", "La câteva minute de Imperfect House"],
      pack_title: "Pachetul de weekend Imperfect House",
      pack: [
        "4 camere cu pat king-size 180 × 200 și baie proprie",
        "Casa întreagă pentru grupuri — până la 12 persoane",
        "Mic dejun la cerere, cu produse locale",
        "La câteva minute de trambulină",
        "Seara — foc în grădină; dimineața — cafea și încă o zi de competiție",
        "Confirmare rapidă pe WhatsApp — fără plată online",
      ],
      motto: "Ziua — zboruri de pe trambulină. Seara — foc în grădină.",
      cta: "Rezervă pe WhatsApp",
      wa_msg: "Bună! Vreau să rezerv pentru FIS Ski Jumping Grand Prix Râșnov (19–20 septembrie 2026). Suntem __ persoane.",
      back: "Înapoi la site",
      verified: "Informații verificate la 11.09.2026 · Sursa: fis-ski.com",
    },
    en: {
      eyebrow: "MAJOR EVENT · 19–20 SEPTEMBER 2026",
      title: "THEY FLY. YOU SLEEP AT IMPERFECT HOUSE.",
      lead: "The official FIS calendar confirms it: on 19–20 September, the world's best ski jumpers come to Râșnov, at the Valea Cărbunării jump — men's and women's Grand Prix. The competition is minutes from the house.",
      highlights: ["FIS Grand Prix — men & women", "Valea Cărbunării ski jump, Râșnov", "The world's best ski jumpers", "Minutes from Imperfect House"],
      pack_title: "The Imperfect House weekend package",
      pack: [
        "4 rooms with 180 × 200 king-size beds and private bathrooms",
        "Entire house for groups — up to 12 guests",
        "Breakfast on request, with local products",
        "Minutes from the ski jump",
        "Evening — fire in the garden; morning — coffee and another day of competition",
        "Fast confirmation on WhatsApp — no online payment",
      ],
      motto: "Daytime — flights off the jump. Evening — fire in the garden.",
      cta: "Book on WhatsApp",
      wa_msg: "Hello! I'd like to book for the FIS Ski Jumping Grand Prix Râșnov (19–20 September 2026). We are __ people.",
      back: "Back to the site",
      verified: "Information verified on 11.09.2026 · Source: fis-ski.com",
    },
    de: {
      eyebrow: "GROSSEVENT · 19.–20. SEPTEMBER 2026",
      title: "SIE FLIEGEN. DU SCHLÄFST IM IMPERFECT HOUSE.",
      lead: "Der offizielle FIS-Kalender bestätigt es: Am 19.–20. September kommen die besten Skispringer der Welt nach Râșnov, auf die Schanze Valea Cărbunării — Grand Prix der Männer und Frauen. Der Wettkampf ist nur wenige Minuten vom Haus entfernt.",
      highlights: ["FIS Grand Prix — Männer & Frauen", "Schanze Valea Cărbunării, Râșnov", "Die besten Skispringer der Welt", "Wenige Minuten vom Imperfect House"],
      pack_title: "Das Imperfect House Wochenend-Paket",
      pack: [
        "4 Zimmer mit King-Size-Betten 180 × 200 und eigenem Bad",
        "Ganzes Haus für Gruppen — bis zu 12 Personen",
        "Frühstück auf Anfrage, mit lokalen Produkten",
        "Wenige Minuten von der Schanze",
        "Abends — Feuer im Garten; morgens — Kaffee und noch ein Wettkampftag",
        "Schnelle Bestätigung per WhatsApp — ohne Online-Zahlung",
      ],
      motto: "Tagsüber — Flüge von der Schanze. Abends — Feuer im Garten.",
      cta: "Per WhatsApp buchen",
      wa_msg: "Hallo! Ich möchte für den FIS Skisprung Grand Prix Râșnov (19.–20. September 2026) buchen. Wir sind __ Personen.",
      back: "Zurück zur Seite",
      verified: "Informationen geprüft am 11.09.2026 · Quelle: fis-ski.com",
    },
    it: {
      eyebrow: "GRANDE EVENTO · 19–20 SETTEMBRE 2026",
      title: "LORO VOLANO. TU DORMI A IMPERFECT HOUSE.",
      lead: "Il calendario ufficiale FIS lo conferma: il 19–20 settembre i migliori saltatori con gli sci del mondo arrivano a Râșnov, sul trampolino di Valea Cărbunării — Grand Prix maschile e femminile. La gara è a pochi minuti da casa.",
      highlights: ["Grand Prix FIS — maschile e femminile", "Trampolino Valea Cărbunării, Râșnov", "I migliori saltatori del mondo", "A pochi minuti da Imperfect House"],
      pack_title: "Il pacchetto weekend Imperfect House",
      pack: [
        "4 camere con letto king-size 180 × 200 e bagno privato",
        "Casa intera per gruppi — fino a 12 persone",
        "Colazione su richiesta, con prodotti locali",
        "A pochi minuti dal trampolino",
        "Di sera — fuoco in giardino; al mattino — caffè e un altro giorno di gare",
        "Conferma rapida su WhatsApp — senza pagamento online",
      ],
      motto: "Di giorno — voli dal trampolino. Di sera — fuoco in giardino.",
      cta: "Prenota su WhatsApp",
      wa_msg: "Buongiorno! Vorrei prenotare per il FIS Ski Jumping Grand Prix Râșnov (19–20 settembre 2026). Siamo __ persone.",
      back: "Torna al sito",
      verified: "Informazioni verificate l'11.09.2026 · Fonte: fis-ski.com",
    },
  },
  "halloween-castelul-bran": {
    ro: {
      eyebrow: "HALLOWEEN IN TRANSYLVANIA · 24 OCT – 1 NOV 2026",
      title: "DORMI LA 20 DE MINUTE DE CASTELUL LUI DRACULA.",
      lead: "Castelul Bran confirmă experiențe tematice între 24 octombrie și 1 noiembrie. Pe 31 octombrie, programul culminant include tur tematic, petrecere, DJ și experiențe speciale; 1 noiembrie este orientat către familii. Iar dimineața te trezești în liniștea Râșnovului.",
      highlights: ["Experiențe tematice 24 oct – 1 nov", "Petrecerea mare: 31 octombrie", "1 noiembrie — program pentru familii", "Bran noaptea. Râșnov dimineața."],
      pack_title: "Pachetul Halloween Imperfect House",
      pack: [
        "4 camere cu pat king-size 180 × 200 și baie proprie",
        "Casa întreagă pentru grupuri — până la 12 persoane",
        "Mic dejun la cerere, cu produse locale",
        "La 20 de minute de Castelul Bran",
        "Pădure, ceață și foc în grădină când povestea se termină",
        "Confirmare rapidă pe WhatsApp — fără plată online",
      ],
      motto: "Noi stingem lumina. De acolo te descurci.",
      cta: "Rezervă Halloween pe WhatsApp",
      wa_msg: "Bună! Vreau să rezerv pentru Halloween la Castelul Bran (24 octombrie – 1 noiembrie 2026). Suntem __ persoane.",
      back: "Înapoi la site",
      verified: "Informații verificate la 11.09.2026 · Sursa: bran-castle.com",
    },
    en: {
      eyebrow: "HALLOWEEN IN TRANSYLVANIA · 24 OCT – 1 NOV 2026",
      title: "SLEEP 20 MINUTES FROM DRACULA'S CASTLE.",
      lead: "Bran Castle confirms themed experiences between 24 October and 1 November. On 31 October the highlight programme includes a themed tour, party, DJ and special experiences; 1 November is family-oriented. And in the morning you wake up in the quiet of Râșnov.",
      highlights: ["Themed experiences 24 Oct – 1 Nov", "The big party: 31 October", "1 November — family programme", "Bran by night. Râșnov in the morning."],
      pack_title: "The Imperfect House Halloween package",
      pack: [
        "4 rooms with 180 × 200 king-size beds and private bathrooms",
        "Entire house for groups — up to 12 guests",
        "Breakfast on request, with local products",
        "20 minutes from Bran Castle",
        "Forest, fog and a garden fire when the story ends",
        "Fast confirmation on WhatsApp — no online payment",
      ],
      motto: "We turn off the light. From there, you're on your own.",
      cta: "Book Halloween on WhatsApp",
      wa_msg: "Hello! I'd like to book for Halloween at Bran Castle (24 October – 1 November 2026). We are __ people.",
      back: "Back to the site",
      verified: "Information verified on 11.09.2026 · Source: bran-castle.com",
    },
    de: {
      eyebrow: "HALLOWEEN IN TRANSSILVANIEN · 24. OKT – 1. NOV 2026",
      title: "SCHLAF 20 MINUTEN VON DRACULAS SCHLOSS ENTFERNT.",
      lead: "Schloss Bran bestätigt thematische Erlebnisse zwischen dem 24. Oktober und dem 1. November. Am 31. Oktober umfasst das Höhepunkt-Programm Themenführung, Party, DJ und besondere Erlebnisse; der 1. November richtet sich an Familien. Und morgens wachst du in der Stille von Râșnov auf.",
      highlights: ["Thematische Erlebnisse 24. Okt – 1. Nov", "Die große Party: 31. Oktober", "1. November — Familienprogramm", "Bran bei Nacht. Râșnov am Morgen."],
      pack_title: "Das Imperfect House Halloween-Paket",
      pack: [
        "4 Zimmer mit King-Size-Betten 180 × 200 und eigenem Bad",
        "Ganzes Haus für Gruppen — bis zu 12 Personen",
        "Frühstück auf Anfrage, mit lokalen Produkten",
        "20 Minuten von Schloss Bran",
        "Wald, Nebel und ein Feuer im Garten, wenn die Geschichte endet",
        "Schnelle Bestätigung per WhatsApp — ohne Online-Zahlung",
      ],
      motto: "Wir machen das Licht aus. Ab da bist du auf dich allein gestellt.",
      cta: "Halloween per WhatsApp buchen",
      wa_msg: "Hallo! Ich möchte für Halloween auf Schloss Bran (24. Oktober – 1. November 2026) buchen. Wir sind __ Personen.",
      back: "Zurück zur Seite",
      verified: "Informationen geprüft am 11.09.2026 · Quelle: bran-castle.com",
    },
    it: {
      eyebrow: "HALLOWEEN IN TRANSILVANIA · 24 OTT – 1 NOV 2026",
      title: "DORMI A 20 MINUTI DAL CASTELLO DI DRACULA.",
      lead: "Il Castello di Bran conferma esperienze a tema tra il 24 ottobre e il 1 novembre. Il 31 ottobre il programma clou include tour tematico, festa, DJ ed esperienze speciali; il 1 novembre è orientato alle famiglie. E al mattino ti svegli nella quiete di Râșnov.",
      highlights: ["Esperienze a tema 24 ott – 1 nov", "La grande festa: 31 ottobre", "1 novembre — programma per famiglie", "Bran di notte. Râșnov al mattino."],
      pack_title: "Il pacchetto Halloween Imperfect House",
      pack: [
        "4 camere con letto king-size 180 × 200 e bagno privato",
        "Casa intera per gruppi — fino a 12 persone",
        "Colazione su richiesta, con prodotti locali",
        "A 20 minuti dal Castello di Bran",
        "Bosco, nebbia e un fuoco in giardino quando la storia finisce",
        "Conferma rapida su WhatsApp — senza pagamento online",
      ],
      motto: "Noi spegniamo la luce. Da lì in poi te la cavi tu.",
      cta: "Prenota Halloween su WhatsApp",
      wa_msg: "Buongiorno! Vorrei prenotare per Halloween al Castello di Bran (24 ottobre – 1 novembre 2026). Siamo __ persone.",
      back: "Torna al sito",
      verified: "Informazioni verificate l'11.09.2026 · Fonte: bran-castle.com",
    },
  },
  "alpin-film-festival": {
    ro: {
      eyebrow: "FESTIVAL DE FILM MONTAN · 21–27 SEPTEMBRIE 2026",
      title: "MUNTELE AJUNGE PE ECRAN. ȘI ECRANUL AJUNGE ÎN RÂȘNOV.",
      lead: "Peste 40 de documentare montane din mai mult de 18 țări, cu proiecții inclusiv la Cinema Amza Pellea din Râșnov. Selecția românească include și un film despre siguranța pe Via Transilvanica. Unul dintre evenimentele care seamănă cel mai mult cu spiritul Imperfect House.",
      highlights: ["Peste 40 de documentare montane", "Filme din 18+ țări", "Proiecții la Cinema Amza Pellea, Râșnov", "Inclusiv un film despre Via Transilvanica"],
      pack_title: "Pachetul de festival Imperfect House",
      pack: [
        "4 camere cu pat king-size 180 × 200 și baie proprie",
        "Casa întreagă pentru grupuri — până la 12 persoane",
        "Mic dejun la cerere, cu produse locale",
        "La câteva minute de Cinema Amza Pellea",
        "Seara — foc în grădină și povești de munte",
        "Confirmare rapidă pe WhatsApp — fără plată online",
      ],
      motto: "Vezi filmul. Dormi la Imperfect. Dimineața ieși și vezi muntele adevărat.",
      cta: "Rezervă pe WhatsApp",
      wa_msg: "Bună! Vreau să rezerv pentru Alpin Film Festival (21–27 septembrie 2026). Suntem __ persoane.",
      back: "Înapoi la site",
      verified: "Informații verificate la 11.09.2026 · Sursa: alpinfilmfestival.ro",
    },
    en: {
      eyebrow: "MOUNTAIN FILM FESTIVAL · 21–27 SEPTEMBER 2026",
      title: "THE MOUNTAIN REACHES THE SCREEN. AND THE SCREEN REACHES RÂȘNOV.",
      lead: "Over 40 mountain documentaries from more than 18 countries, with screenings also at Cinema Amza Pellea in Râșnov. The Romanian selection includes a film about safety on Via Transilvanica. One of the events that best matches the Imperfect House spirit.",
      highlights: ["Over 40 mountain documentaries", "Films from 18+ countries", "Screenings at Cinema Amza Pellea, Râșnov", "Including a film about Via Transilvanica"],
      pack_title: "The Imperfect House festival package",
      pack: [
        "4 rooms with 180 × 200 king-size beds and private bathrooms",
        "Entire house for groups — up to 12 guests",
        "Breakfast on request, with local products",
        "Minutes from Cinema Amza Pellea",
        "Evening — fire in the garden and mountain stories",
        "Fast confirmation on WhatsApp — no online payment",
      ],
      motto: "Watch the film. Sleep at Imperfect. In the morning, step out and see the real mountain.",
      cta: "Book on WhatsApp",
      wa_msg: "Hello! I'd like to book for Alpin Film Festival (21–27 September 2026). We are __ people.",
      back: "Back to the site",
      verified: "Information verified on 11.09.2026 · Source: alpinfilmfestival.ro",
    },
    de: {
      eyebrow: "BERGFILMFESTIVAL · 21.–27. SEPTEMBER 2026",
      title: "DER BERG KOMMT AUFS LEINWAND. UND DIE LEINWAND KOMMT NACH RÂȘNOV.",
      lead: "Über 40 Bergdokumentationen aus mehr als 18 Ländern, mit Vorführungen auch im Kino Amza Pellea in Râșnov. Die rumänische Auswahl umfasst auch einen Film über Sicherheit auf der Via Transilvanica. Eines der Events, die am besten zum Imperfect-House-Geist passen.",
      highlights: ["Über 40 Bergdokumentationen", "Filme aus 18+ Ländern", "Vorführungen im Kino Amza Pellea, Râșnov", "Darunter ein Film über die Via Transilvanica"],
      pack_title: "Das Imperfect House Festival-Paket",
      pack: [
        "4 Zimmer mit King-Size-Betten 180 × 200 und eigenem Bad",
        "Ganzes Haus für Gruppen — bis zu 12 Personen",
        "Frühstück auf Anfrage, mit lokalen Produkten",
        "Wenige Minuten vom Kino Amza Pellea",
        "Abends — Feuer im Garten und Berggeschichten",
        "Schnelle Bestätigung per WhatsApp — ohne Online-Zahlung",
      ],
      motto: "Schau den Film. Schlaf im Imperfect. Morgens gehst du raus und siehst den echten Berg.",
      cta: "Per WhatsApp buchen",
      wa_msg: "Hallo! Ich möchte für das Alpin Film Festival (21.–27. September 2026) buchen. Wir sind __ Personen.",
      back: "Zurück zur Seite",
      verified: "Informationen geprüft am 11.09.2026 · Quelle: alpinfilmfestival.ro",
    },
    it: {
      eyebrow: "FESTIVAL DEL CINEMA DI MONTAGNA · 21–27 SETTEMBRE 2026",
      title: "LA MONTAGNA ARRIVA SULLO SCHERMO. E LO SCHERMO ARRIVA A RÂȘNOV.",
      lead: "Oltre 40 documentari di montagna da più di 18 paesi, con proiezioni anche al Cinema Amza Pellea di Râșnov. La selezione rumena include anche un film sulla sicurezza sulla Via Transilvanica. Uno degli eventi più vicini allo spirito di Imperfect House.",
      highlights: ["Oltre 40 documentari di montagna", "Film da 18+ paesi", "Proiezioni al Cinema Amza Pellea, Râșnov", "Incluso un film sulla Via Transilvanica"],
      pack_title: "Il pacchetto festival Imperfect House",
      pack: [
        "4 camere con letto king-size 180 × 200 e bagno privato",
        "Casa intera per gruppi — fino a 12 persone",
        "Colazione su richiesta, con prodotti locali",
        "A pochi minuti dal Cinema Amza Pellea",
        "Di sera — fuoco in giardino e storie di montagna",
        "Conferma rapida su WhatsApp — senza pagamento online",
      ],
      motto: "Guarda il film. Dormi a Imperfect. Al mattino esci e vedi la montagna vera.",
      cta: "Prenota su WhatsApp",
      wa_msg: "Buongiorno! Vorrei prenotare per l'Alpin Film Festival (21–27 settembre 2026). Siamo __ persone.",
      back: "Torna al sito",
      verified: "Informazioni verificate l'11.09.2026 · Fonte: alpinfilmfestival.ro",
    },
  },
  "maraton-piatra-craiului": {
    ro: {
      eyebrow: "ALERGARE MONTANĂ · 26–27 SEPTEMBRIE 2026",
      title: "39,7 KM PRIN PIATRA CRAIULUI.",
      lead: "Una dintre competițiile istorice de alergare montană din România: traseul principal are 39,7 km și ±2.250 m diferență de nivel, iar duminică există și MPC Junior pentru copii. Startul e la Zărnești, la un sfert de oră de casă.",
      highlights: ["Traseu 39,7 km · ±2.250 m", "Una dintre cursele istorice ale României", "MPC Junior pentru copii, duminică", "Start la Zărnești — la 15 minute de casă"],
      pack_title: "Pachetul de alergător Imperfect House",
      pack: [
        "4 camere cu pat king-size 180 × 200 și baie proprie",
        "Casa întreagă pentru echipe — până la 12 persoane",
        "Mic dejun la cerere, cu produse locale",
        "La 15 minute de startul din Zărnești",
        "Duș cald, grădină și liniște pentru recuperare",
        "Confirmare rapidă pe WhatsApp — fără plată online",
      ],
      motto: "După asta, patul de 180×200 nu mai este facilitate. Este premiu.",
      cta: "Rezervă pe WhatsApp",
      wa_msg: "Bună! Vreau să rezerv pentru Maraton Piatra Craiului (26–27 septembrie 2026). Suntem __ persoane.",
      back: "Înapoi la site",
      verified: "Informații verificate la 11.09.2026 · Sursa: maratonpiatracraiului.ro",
    },
    en: {
      eyebrow: "MOUNTAIN RUNNING · 26–27 SEPTEMBER 2026",
      title: "39.7 KM THROUGH PIATRA CRAIULUI.",
      lead: "One of Romania's historic mountain running races: the main course is 39.7 km with ±2,250 m of elevation, and on Sunday there is also MPC Junior for kids. The start is in Zărnești, a quarter of an hour from the house.",
      highlights: ["39.7 km course · ±2,250 m", "One of Romania's historic races", "MPC Junior for kids on Sunday", "Start in Zărnești — 15 minutes from the house"],
      pack_title: "The Imperfect House runner's package",
      pack: [
        "4 rooms with 180 × 200 king-size beds and private bathrooms",
        "Entire house for teams — up to 12 guests",
        "Breakfast on request, with local products",
        "15 minutes from the Zărnești start line",
        "Hot shower, garden and quiet for recovery",
        "Fast confirmation on WhatsApp — no online payment",
      ],
      motto: "After that, the 180×200 bed is no longer an amenity. It's the prize.",
      cta: "Book on WhatsApp",
      wa_msg: "Hello! I'd like to book for Maraton Piatra Craiului (26–27 September 2026). We are __ people.",
      back: "Back to the site",
      verified: "Information verified on 11.09.2026 · Source: maratonpiatracraiului.ro",
    },
    de: {
      eyebrow: "BERGLAUF · 26.–27. SEPTEMBER 2026",
      title: "39,7 KM DURCH DEN PIATRA CRAIULUI.",
      lead: "Einer der historischen Bergläufe Rumäniens: Die Hauptstrecke ist 39,7 km lang mit ±2.250 m Höhendifferenz, und am Sonntag gibt es auch den MPC Junior für Kinder. Der Start ist in Zărnești, eine Viertelstunde vom Haus.",
      highlights: ["Strecke 39,7 km · ±2.250 m", "Einer der historischen Läufe Rumäniens", "MPC Junior für Kinder am Sonntag", "Start in Zărnești — 15 Minuten vom Haus"],
      pack_title: "Das Imperfect House Läufer-Paket",
      pack: [
        "4 Zimmer mit King-Size-Betten 180 × 200 und eigenem Bad",
        "Ganzes Haus für Teams — bis zu 12 Personen",
        "Frühstück auf Anfrage, mit lokalen Produkten",
        "15 Minuten vom Start in Zărnești",
        "Heiße Dusche, Garten und Ruhe zur Erholung",
        "Schnelle Bestätigung per WhatsApp — ohne Online-Zahlung",
      ],
      motto: "Danach ist das 180×200-Bett keine Annehmlichkeit mehr. Es ist der Preis.",
      cta: "Per WhatsApp buchen",
      wa_msg: "Hallo! Ich möchte für den Maraton Piatra Craiului (26.–27. September 2026) buchen. Wir sind __ Personen.",
      back: "Zurück zur Seite",
      verified: "Informationen geprüft am 11.09.2026 · Quelle: maratonpiatracraiului.ro",
    },
    it: {
      eyebrow: "CORSA IN MONTAGNA · 26–27 SETTEMBRE 2026",
      title: "39,7 KM ATTRAVERSO IL PIATRA CRAIULUI.",
      lead: "Una delle storiche gare di corsa in montagna della Romania: il percorso principale misura 39,7 km con ±2.250 m di dislivello, e la domenica c'è anche la MPC Junior per i bambini. La partenza è a Zărnești, a un quarto d'ora da casa.",
      highlights: ["Percorso 39,7 km · ±2.250 m", "Una delle gare storiche della Romania", "MPC Junior per i bambini, domenica", "Partenza a Zărnești — 15 minuti da casa"],
      pack_title: "Il pacchetto del corridore Imperfect House",
      pack: [
        "4 camere con letto king-size 180 × 200 e bagno privato",
        "Casa intera per squadre — fino a 12 persone",
        "Colazione su richiesta, con prodotti locali",
        "A 15 minuti dalla partenza di Zărnești",
        "Doccia calda, giardino e quiete per il recupero",
        "Conferma rapida su WhatsApp — senza pagamento online",
      ],
      motto: "Dopo, il letto 180×200 non è più un comfort. È il premio.",
      cta: "Prenota su WhatsApp",
      wa_msg: "Buongiorno! Vorrei prenotare per la Maraton Piatra Craiului (26–27 settembre 2026). Siamo __ persone.",
      back: "Torna al sito",
      verified: "Informazioni verificate l'11.09.2026 · Fonte: maratonpiatracraiului.ro",
    },
  },
  "targul-de-craciun-brasov": {
    ro: {
      eyebrow: "CHRISTMAS IN TRANSYLVANIA · DATE ÎN CURS DE CONFIRMARE",
      title: "VIN FIERT ÎN PIAȚA SFATULUI. FOC ȘI LINIȘTE LA RÂȘNOV.",
      lead: "Sursele indică sfârșitul lui noiembrie 2026 – începutul lui ianuarie 2027, orientativ 27 noiembrie – 3 ianuarie; data finală va fi actualizată imediat după anunțul oficial al Primăriei Brașov. Până atunci, pre-rezervările sunt deschise.",
      highlights: ["Târgul de Crăciun din Piața Sfatului", "Sf. noiembrie – începutul lui ianuarie (de confirmat)", "Zăpadă, dacă avem noroc", "La 25 de minute de casă"],
      pack_title: "Pachetul de iarnă Imperfect House",
      pack: [
        "4 camere cu pat king-size 180 × 200 și baie proprie",
        "Casa întreagă pentru familii și grupuri — până la 12 persoane",
        "Mic dejun la cerere, cu produse locale",
        "La 25 de minute de Piața Sfatului",
        "Grădină, foc de seară și liniște după târg",
        "Confirmare rapidă pe WhatsApp — fără plată online",
      ],
      motto: "Brașovul luminează. Râșnovul te lasă să dormi.",
      cta: "Pre-rezervă pe WhatsApp",
      wa_msg: "Bună! Vreau să pre-rezerv pentru Târgul de Crăciun Brașov 2026 (sfârșit nov – început ian, date de confirmat). Suntem __ persoane.",
      back: "Înapoi la site",
      verified: "Informații verificate la 11.09.2026 · Datele finale urmează anunțul oficial",
    },
    en: {
      eyebrow: "CHRISTMAS IN TRANSYLVANIA · DATES TO BE CONFIRMED",
      title: "MULLED WINE IN THE COUNCIL SQUARE. FIRE AND QUIET IN RÂȘNOV.",
      lead: "Sources point to late November 2026 – early January 2027, roughly 27 November – 3 January; the final dates will be updated right after Brașov City Hall's official announcement. Until then, pre-bookings are open.",
      highlights: ["The Christmas Market in the Council Square", "Late Nov – early Jan (to be confirmed)", "Snow, if we're lucky", "25 minutes from the house"],
      pack_title: "The Imperfect House winter package",
      pack: [
        "4 rooms with 180 × 200 king-size beds and private bathrooms",
        "Entire house for families and groups — up to 12 guests",
        "Breakfast on request, with local products",
        "25 minutes from the Council Square",
        "Garden, evening fire and quiet after the market",
        "Fast confirmation on WhatsApp — no online payment",
      ],
      motto: "Brașov lights up. Râșnov lets you sleep.",
      cta: "Pre-book on WhatsApp",
      wa_msg: "Hello! I'd like to pre-book for the Brașov Christmas Market 2026 (late Nov – early Jan, dates to be confirmed). We are __ people.",
      back: "Back to the site",
      verified: "Information verified on 11.09.2026 · Final dates follow the official announcement",
    },
    de: {
      eyebrow: "CHRISTMAS IN TRANSYLVANIA · TERMINE FOLGEN",
      title: "GLÜHWEIN AUF DEM RATHAUSPLATZ. FEUER UND RUHE IN RÂȘNOV.",
      lead: "Quellen deuten auf Ende November 2026 – Anfang Januar 2027, ungefähr 27. November – 3. Januar; die finalen Termine werden sofort nach der offiziellen Ankündigung der Stadt Brașov aktualisiert. Bis dahin sind Vorreservierungen offen.",
      highlights: ["Der Weihnachtsmarkt auf dem Rathausplatz", "Ende Nov – Anfang Jan (folgt)", "Schnee, wenn wir Glück haben", "25 Minuten vom Haus"],
      pack_title: "Das Imperfect House Winter-Paket",
      pack: [
        "4 Zimmer mit King-Size-Betten 180 × 200 und eigenem Bad",
        "Ganzes Haus für Familien und Gruppen — bis zu 12 Personen",
        "Frühstück auf Anfrage, mit lokalen Produkten",
        "25 Minuten vom Rathausplatz",
        "Garten, Abendfeuer und Ruhe nach dem Markt",
        "Schnelle Bestätigung per WhatsApp — ohne Online-Zahlung",
      ],
      motto: "Brașov leuchtet. Râșnov lässt dich schlafen.",
      cta: "Per WhatsApp vorreservieren",
      wa_msg: "Hallo! Ich möchte für den Brașover Weihnachtsmarkt 2026 (Ende Nov – Anfang Jan, Termine folgen) vorreservieren. Wir sind __ Personen.",
      back: "Zurück zur Seite",
      verified: "Informationen geprüft am 11.09.2026 · Die finalen Termine folgen der offiziellen Ankündigung",
    },
    it: {
      eyebrow: "CHRISTMAS IN TRANSYLVANIA · DATE DA CONFERMARE",
      title: "VIN BRULÉ IN PIAZZA DEL CONSIGLIO. FUOCO E QUIETE A RÂȘNOV.",
      lead: "Le fonti indicano fine novembre 2026 – inizio gennaio 2027, orientativamente 27 novembre – 3 gennaio; le date definitive saranno aggiornate subito dopo l'annuncio ufficiale del Comune di Brașov. Nel frattempo, le pre-prenotazioni sono aperte.",
      highlights: ["Il Mercatino di Natale in Piazza del Consiglio", "Fine nov – inizio gen (da confermare)", "Neve, se siamo fortunati", "A 25 minuti da casa"],
      pack_title: "Il pacchetto invernale Imperfect House",
      pack: [
        "4 camere con letto king-size 180 × 200 e bagno privato",
        "Casa intera per famiglie e gruppi — fino a 12 persone",
        "Colazione su richiesta, con prodotti locali",
        "A 25 minuti da Piazza del Consiglio",
        "Giardino, fuoco serale e quiete dopo il mercatino",
        "Conferma rapida su WhatsApp — senza pagamento online",
      ],
      motto: "Brașov si illumina. Râșnov ti lascia dormire.",
      cta: "Pre-prenota su WhatsApp",
      wa_msg: "Buongiorno! Vorrei pre-prenotare per il Mercatino di Natale di Brașov 2026 (fine nov – inizio gen, date da confermare). Siamo __ persone.",
      back: "Torna al sito",
      verified: "Informazioni verificate l'11.09.2026 · Le date definitive seguono l'annuncio ufficiale",
    },
  },
};

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" }, transition: { duration: 0.8, delay },
});

const DEFAULT_PREFILL = { unitType: "room", checkIn: "", checkOut: "", adults: 2, children: 0, pet: false };

export default function EventPage() {
  const { slug } = useParams();
  const { lang } = useLang();
  const page = PAGES[slug];
  const ev = EVENTS.find((e) => e.slug === slug);
  const c = page ? page[lang] || page.en : null;
  const units = UNITS.filter((u) => u.active);
  const wa = CONFIG.whatsapp || "40721663181";

  useEffect(() => {
    const toTop = () => {
      if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
      window.scrollTo(0, 0);
    };
    toTop();
    const raf = requestAnimationFrame(toTop);
    return () => cancelAnimationFrame(raf);
  }, [slug]);

  if (!page || !ev) {
    return (
      <div className="relative" data-testid="event-page-missing">
        <Header config={CONFIG} />
        <main className="pt-40 pb-24 text-center">
          <Link to="/" className="text-terracotta font-semibold">{`<- Imperfect House`}</Link>
        </main>
        <Footer config={CONFIG} />
      </div>
    );
  }

  return (
    <div className="relative" data-testid={`event-page-${slug}`}>
      <Header config={CONFIG} />
      <main className="pt-28 md:pt-36 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-brown/60 hover:text-terracotta transition-colors" data-testid="event-back">
            <ArrowLeft size={16} /> {c.back}
          </Link>

          <div className="mt-8 grid lg:grid-cols-12 gap-10 items-start">
            <motion.div {...fadeUp()} className="lg:col-span-5">
              <button onClick={() => scrollToId("#rezervare")} data-testid="event-poster" aria-label={c.cta}
                className="block w-full overflow-hidden rounded-3xl shadow-2xl shadow-main/20 cursor-pointer group">
                <img src={ev.image} alt={ev.name} className="w-full h-auto group-hover:scale-[1.02] transition-transform duration-700" />
              </button>
              <p className="mt-4 text-[11px] text-brown/50 font-light" data-testid="event-verified-note">{c.verified}</p>
            </motion.div>

            <div className="lg:col-span-7">
              <motion.p {...fadeUp(0.1)} className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-terracotta flex items-center gap-3" data-testid="event-eyebrow">
                <Sparkles size={15} /> {c.eyebrow}
              </motion.p>
              <motion.h1 {...fadeUp(0.15)} className="mt-4 font-serif font-medium text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-main" data-testid="event-title">
                {c.title}
              </motion.h1>
              <motion.p {...fadeUp(0.2)} className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-semibold text-walnut uppercase tracking-wider" data-testid="event-dates">
                <span className="inline-flex items-center gap-2"><CalendarDays size={15} className="text-terracotta" /> {ev.start_date} → {ev.end_date}</span>
                <span className="inline-flex items-center gap-2"><MapPin size={15} className="text-sage" /> {ev[`location_${lang}`] || ev.location_en || ev.location_ro}</span>
              </motion.p>
              <motion.p {...fadeUp(0.25)} className="mt-5 text-brown/85 font-light leading-relaxed max-w-2xl" data-testid="event-lead">
                {c.lead}
              </motion.p>

              <motion.div {...fadeUp(0.3)} className="mt-8 flex flex-wrap gap-2" data-testid="event-highlights">
                {c.highlights.map((h) => (
                  <span key={h} className="rounded-full bg-sage/20 text-green-dark text-xs font-semibold px-4 py-2">{h}</span>
                ))}
              </motion.div>

              <motion.div {...fadeUp(0.35)} className="mt-10 bg-cream-light border border-warm-grey/40 rounded-3xl p-7 md:p-8" data-testid="event-package">
                <p className="font-serif text-2xl text-main">{c.pack_title}</p>
                <ul className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                  {c.pack.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-brown/85 font-light">
                      <Check size={16} className="text-terracotta shrink-0 mt-0.5" /> {item}
                    </li>
                  ))}
                </ul>
                <p className="mt-6 font-serif italic text-lg text-walnut">{c.motto}</p>
                <div className="mt-6 flex flex-wrap items-center gap-4">
                  <a href={waLink(wa, c.wa_msg)} target="_blank" rel="noreferrer" data-testid="event-whatsapp-cta"
                    className="inline-flex items-center gap-2 rounded-full bg-terracotta text-cream-light font-semibold px-7 py-3.5 hover:bg-terracotta/90 transition-all duration-300 text-sm">
                    {c.cta}
                  </a>
                  <button onClick={() => scrollToId("#rezervare")} data-testid="event-calendar-cta"
                    className="inline-flex items-center gap-2 rounded-full border border-terracotta/60 text-terracotta font-semibold px-7 py-3.5 hover:bg-terracotta hover:text-cream-light transition-all duration-300 text-sm">
                    {t_safe(lang)}
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        <BookingSection units={units} config={CONFIG} prefill={DEFAULT_PREFILL} />
      </main>
      <Footer config={CONFIG} />
      <WhatsAppFloat config={CONFIG} />
    </div>
  );
}

function t_safe(lang) {
  const labels = { ro: "Vezi calendarul de disponibilitate", en: "See the availability calendar", de: "Zum Verfügbarkeitskalender", it: "Vedi il calendario delle disponibilità" };
  return labels[lang] || labels.en;
}
